import classNames from "classnames";
import { NEW_FOLDER_MESSAGE } from "constants/documents";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { useState } from "react";
import {
  useCreateFolderMutation,
  useGetEntityFolderQuery,
  useGetFolderContentQuery,
  useUploadFileMutation,
} from "store/apis/documents";
import { BackendError } from "types/utils/error";
import { FileIcon } from "components/design/FileIcon";
import Async from "./DesignSystem/AsyncComponents/Async";
import { Button } from "./DesignSystem/Button/Button";
import Modal from "./DesignSystem/Modal/Modal";
import { AddNewFolder } from "./documents/documentsModal/AddNewFolder";
import { BreadcrumbDND } from "./documents/FileDnDWrapper";
import Breadcrumb from "./DesignSystem/Breadcrumb/Breadcrumb";
import { SelecteFile } from "types/Models/documents";
import { Skeleton } from "./DesignSystem/Skeleton/Skeleton";

type SelectFolderForUploadProp = {
  showModal: boolean;
  closeModal: () => void;
  files?: File[];
  onUploadSuccess?: (file: SelecteFile) => void;
  onUpload?: (folderId: string) => void;
  entityId?: string;
  folderPath?: string[];
};

export const SelectFolderForUpload = ({
  showModal,
  closeModal,
  files,
  onUploadSuccess,
  onUpload,
  entityId,
  folderPath,
}: SelectFolderForUploadProp) => {
  const { uuid: groupId, documents_group_id } = useCurrentGroupContext();
  const { alertToast, successToast } = useToast();
  const [createFolder] = useCreateFolderMutation();
  const [uploadFiles, { isLoading: isUploading }] = useUploadFileMutation();
  const newFolderModal = useModal();
  const [navigatedFolderId, setNavigatedFolderId] = useState<string | null>(
    null
  );
  const [uploadTargetFolderId, setUploadTargetFolderId] = useState<
    string | null
  >(null);

  const {
    data: entityFolder,
    isSuccess: isEntityFolderSuccess,
    isError: isEntityFolderError,
  } = useGetEntityFolderQuery(
    { entityId: entityId || "", folderPath: folderPath || [] },
    { skip: !entityId || !folderPath?.length || !showModal }
  );
  const entityFolderId = entityFolder?.uuid;

  const isEntityFolderPending =
    Boolean(entityId && folderPath?.length) &&
    !isEntityFolderSuccess &&
    !isEntityFolderError;

  const currentFolderId =
    navigatedFolderId ||
    entityFolderId ||
    (isEntityFolderPending ? "" : documents_group_id);
  const targetFolderId = uploadTargetFolderId || currentFolderId;

  const {
    data: folderData,
    isLoading,
    isSuccess,
    isFetching,
    refetch,
  } = useGetFolderContentQuery(
    { groupId, folderId: currentFolderId },
    { skip: !currentFolderId }
  );

  const { subfolders: folderContent = [], parents_list = [] } =
    folderData || {};
  const isEmptyFolder = folderContent.length === 0 && isSuccess;
  const currentFolder = parents_list[parents_list.length - 1];
  const isBaseFolder = currentFolder?.uuid === documents_group_id;
  const breadcrumbParents = parents_list.slice(0, -1);

  const selectedFolderName =
    folderContent.find((folder) => folder.uuid === uploadTargetFolderId)
      ?.name || currentFolder?.name;

  const handleFolderNavigation = (folderId: string) => {
    setNavigatedFolderId(folderId);
    setUploadTargetFolderId(null);
  };

  const handleFolderSelect = (folderId: string) => {
    setUploadTargetFolderId(folderId);
  };

  const handleFileUpload = async () => {
    if (onUpload) {
      closeModal();
      return onUpload(targetFolderId);
    }

    try {
      const uploadedFiles = await uploadFiles({
        files: files || [],
        groupId,
        parentFolderId: targetFolderId,
      }).unwrap();

      const firstFile = uploadedFiles[0];
      onUploadSuccess?.({
        file_type: firstFile.file_type,
        is_previewable: firstFile.is_previewable,
        name: firstFile.name,
        uuid: firstFile.uuid,
      });
      closeModal();
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  const handleCreateFolder = async ({
    folder_name,
  }: {
    folder_name: string;
  }) => {
    try {
      const { uuid } = await createFolder({
        folder_name,
        groupId,
        parentFolderId: currentFolderId,
      }).unwrap();
      successToast({ message: NEW_FOLDER_MESSAGE });
      newFolderModal.close();
      refetch();
      setUploadTargetFolderId(uuid);
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  const showBreadcrumbs = !isBaseFolder && breadcrumbParents.length > 0;

  return (
    <>
      <Modal.Root open={showModal} onOpenChange={closeModal}>
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>
              <div className="t-flex t-flex-col t-gap-1">
                {showBreadcrumbs && (
                  <div className="t-mr-2">
                    <Breadcrumb.Root>
                      {breadcrumbParents.map((parent) => (
                        <Breadcrumb.Item
                          as="button"
                          key={parent.uuid}
                          onClick={() => handleFolderNavigation(parent.uuid)}
                        >
                          <BreadcrumbDND
                            folderId={parent.uuid}
                            name={parent.name}
                            isPrivate={parent.is_private}
                            onDrop={() => {}}
                          />
                        </Breadcrumb.Item>
                      ))}
                    </Breadcrumb.Root>
                  </div>
                )}
                <div className="t-flex">
                  Upload to: &nbsp;
                  <div className="t-w-80 t-line-clamp-1">
                    {selectedFolderName}
                  </div>
                </div>
              </div>
            </Modal.Title>
            <Modal.Close />
          </Modal.Header>
          <Modal.Body>
            <Async.Root
              isEmpty={isEmptyFolder}
              isLoading={isLoading || isFetching}
              isSuccess={isSuccess}
              renderOnLoad={
                <div className="t-flex t-h-80 t-flex-col t-items-start t-gap-1 t-select-none t-animate-pulse">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton className="t-w-full t-px-2 t-py-1" key={i}>
                      <FileIcon fileType="FOLDER" size="32" />
                    </Skeleton>
                  ))}
                </div>
              }
            >
              <Async.Empty>
                <div className="t-flex t-flex-col t-gap-2 t-justify-center t-items-center t-w-full t-text-neutral-40 t-h-80">
                  This folder is empty
                  <Button
                    customType="secondary"
                    size="small"
                    onClick={newFolderModal.open}
                  >
                    Create folder
                  </Button>
                </div>
              </Async.Empty>
              <Async.Success>
                <div className="t-flex t-h-80 t-flex-col t-items-start t-gap-1 t-select-none">
                  {folderContent.map((folder) => {
                    const isSelected = uploadTargetFolderId === folder.uuid;

                    return (
                      <button
                        key={folder.uuid}
                        disabled={isFetching}
                        onClick={() => handleFolderSelect(folder.uuid)}
                        onDoubleClick={() =>
                          handleFolderNavigation(folder.uuid)
                        }
                        className={classNames(
                          "all:unset t-flex t-w-full t-cursor-pointer t-items-center t-gap-2 t-border-solid t-border-surface-transparent t-px-2 t-py-1 hover:t-rounded hover:t-bg-purple-10 focus-visible:t-rounded focus-visible:t-bg-purple-10 focus-visible:t-outline focus-visible:t-outline-purple-30",
                          {
                            "t-rounded t-border-solid t-border-purple-30 t-bg-purple-10":
                              isSelected,
                          }
                        )}
                      >
                        <FileIcon fileType={folder.file_type} size="32" />
                        <div className="t-max-w-[25rem] t-truncate t-text-body-sm">
                          {folder.name}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </Async.Success>
            </Async.Root>
          </Modal.Body>
          <Modal.Footer className="t-flex t-justify-between t-items-center">
            <Button
              customType="secondary"
              size="small"
              onClick={newFolderModal.open}
            >
              Create folder
            </Button>
            <div className="t-flex t-gap-2">
              <Button size="small" onClick={closeModal}>
                Cancel
              </Button>
              <Button
                customType="primary"
                size="small"
                disabled={isUploading}
                isLoading={isUploading}
                onClick={handleFileUpload}
              >
                Upload
              </Button>
            </div>
          </Modal.Footer>
        </Modal.Content>
      </Modal.Root>
      {newFolderModal.isOpen && (
        <AddNewFolder
          isOpen={newFolderModal.isOpen}
          close={newFolderModal.close}
          createFolder={handleCreateFolder}
        />
      )}
    </>
  );
};
